import { computed } from "vue";
import { useTableContext } from "./useTableContext";
import { useTableSorting } from "./useTableSorting";
import { useTableSelection } from "./useTableSelection";
import { useTableResize } from "./useTableResize";
import { useTableLayout } from "./useTableLayout";

// Re-export types and constants so consumers can import from a single entry point.
export {
  TABLE_TYPE,
  TABLE_HEAD_TYPE,
  TABLE_BODY_TYPE,
  TABLE_FOOTER_TYPE,
  TABLE_ROW_TYPE,
  TABLE_CELL_TYPE,
  TABLE_HEADER_CELL_TYPE,
  SortDirection,
} from "./useTable.types";

export type { ColumnWidth, TableContext } from "./useTable.types";

/**
 * Table controller composable.
 *
 * Composes all table concerns (sorting, selection, resizing, layout) into a
 * single public API. Each table sub-component calls `useTable()` during setup
 * and destructures only the slice of functionality it needs.
 *
 * All shared state is stored in the stateStore (via `useSharedState` in each
 * component) so every `useTable()` instance resolves the same underlying data.
 *
 * ## Architecture
 *
 * ```
 * useTable()                  ← Public API (this file)
 * ├── useTableContext()       ← Foundation: table resolution, indices, header cells
 * ├── useTableSorting(ctx)   ← Sort-by-column logic
 * ├── useTableSelection(ctx) ← Row selection (single / multiple)
 * ├── useTableResize(ctx)    ← Column drag-to-resize
 * └── useTableLayout(ctx)    ← CSS grid layout, scroll styles
 * ```
 *
 * ## Adding a new concern
 *
 * 1. Create `useTableXxx.ts` — accept `TableContext` as the first parameter.
 * 2. Return only what components need.
 * 3. Compose it in this file and add its exports to the return object.
 * 4. **Do not** call `useComponentContext()` or `useStateStore()` directly in
 *    sub-composables — use the shared `ctx` instead.
 *
 * @example
 * ```ts
 * // In a new useTableFiltering.ts:
 * export function useTableFiltering(ctx: TableContext) {
 *   const { tableProps, bodyUid, stateStore } = ctx;
 *   // ...filtering logic...
 *   return { activeFilters, applyFilter, clearFilters };
 * }
 *
 * // In this file:
 * const { activeFilters, applyFilter, clearFilters } = useTableFiltering(ctx);
 * ```
 */
export const useTable = () => {
  const ctx = useTableContext();

  const { isCellSortable, sortColumn } = useTableSorting(ctx);
  const { isRowSelected, isRowSelectable, getSelectedRows, syncRowSelection, handleRowClick } = useTableSelection(ctx);
  const { cellRef, isColumnResizable, isLastColumn, getStoredColumnWidths, startResize } = useTableResize(ctx);
  const { scrollMode, columnWidthStyle, bodyMaxHeightStyle } = useTableLayout(ctx, getStoredColumnWidths);

  return {
    // Table (Table.vue)
    columnWidthStyle,
    getSelectedRows,

    // Body (TableBody.vue)
    scrollMode,
    bodyMaxHeightStyle,

    // Row (TableRow.vue)
    syncRowSelection,
    handleRowClick,
    isRowSelected,
    isRowSelectable,
    isEvenRow: computed(() => ctx.currentRowIndex.value % 2 === 1),
    isTableStriped: computed(() => !!ctx.tableProps.value?.striped),
    isTableHoverable: computed(() => !!ctx.tableProps.value?.hoverable),

    // Header cell (TableHeaderCell.vue)
    cellRef,
    isCellSortable,
    isColumnResizable,
    isLastColumn,
    sortColumn,
    startResize,
  };
};
